// src/pages/Dashboard/Admin/Messages/components/MessageDetailsDrawer.jsx

import React from "react";
import { FaComments, FaTimes, FaTrash } from "react-icons/fa";

import {
  formatDateTimeFR,
  getSourceMeta,
  getSubjectBadgeClass,
} from "../utils/messagesDashboardUtils";

function DetailLine({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950">
      <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
        {label}
      </p>

      <p className="mt-1 text-sm font-bold text-slate-900 dark:text-white">
        {value || "—"}
      </p>
    </div>
  );
}

export default function MessageDetailsDrawer({
  isOpen,
  message,
  deletingKey,
  onClose,
  onDelete,
}) {
  if (!isOpen || !message) return null;

  const sourceMeta = getSourceMeta(message.source);

  const subjectBadge =
    message.source === "private"
      ? getSubjectBadgeClass(message.subject)
      : sourceMeta.badge;

  return (
    <div className="fixed inset-0 z-[9999]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Détail message
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaComments className="text-indigo-600 dark:text-indigo-300" />
              {message.source === "chat" ? "Message de chat" : "Message privé"}
            </h2>

            <div className="mt-3 flex flex-wrap gap-2">
              <span
                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${sourceMeta.badge}`}
              >
                {sourceMeta.label}
              </span>

              <span
                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${subjectBadge}`}
                style={
                  message.source === "chat" && message.groupColor
                    ? {
                        backgroundColor: message.groupColor,
                        color: "#fff",
                        borderColor: message.groupColor,
                      }
                    : undefined
                }
              >
                {message.subject}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <DetailLine label="Expéditeur" value={message.senderName} />

            <DetailLine
              label={message.source === "chat" ? "Canal" : "Destinataire"}
              value={message.recipientName}
            />

            <DetailLine label="Date" value={formatDateTimeFR(message.date)} />

            <DetailLine label="Source" value={sourceMeta.label} />
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
            <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
              Contenu
            </p>

            <div className="mt-3 whitespace-pre-wrap rounded-2xl bg-slate-50 p-4 text-sm leading-7 text-slate-700 dark:bg-slate-900 dark:text-slate-200">
              {message.content || "—"}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaTimes />
              Fermer
            </button>

            <button
              type="button"
              onClick={() => onDelete(message)}
              disabled={deletingKey === message.key}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
            >
              <FaTrash />
              Supprimer
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}